import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/shared/api';

/**
 * Сколько примеров реальных значений показывать в ячейке «Значения» атрибута `name`.
 */
export const ENTITY_TYPE_SAMPLE_LIMIT = 5;

/**
 * Раздел приложения, соответствующий типу сущности онтологии: маршрут с полным
 * списком и загрузчик первых нескольких человекочитаемых значений (для примеров).
 */
type EntityTypeSection = {
  /** Маршрут раздела с полным списком (для иконки-ссылки «→»). */
  route: string;
  /** Загружает первые `limit` названий объектов раздела — примеры значений атрибута `name`. */
  fetchSamples: (limit: number) => Promise<string[]>;
};

// Достаёт первые `limit` непустых `name` из плоского списочного ответа `{ [key]: [{ name }] }`.
const fetchFlatNames = async (
  path: string,
  itemsKey: string,
  params: Record<string, unknown>,
  limit: number,
): Promise<string[]> => {
  const { data } = await apiClient.get<Record<string, unknown>>(path, { params });
  const items = (data?.[itemsKey] as { name?: string | null }[] | undefined) ?? [];
  return items
    .map((item) => item.name)
    .filter((name): name is string => !!name)
    .slice(0, limit);
};

// Узел дерева компетенций (только нужные для примеров поля).
type CompetencyTreeNode = { name?: string | null; children?: CompetencyTreeNode[] };

// Обходит дерево компетенций в глубину и собирает первые `limit` названий.
const fetchCompetencyNames = async (limit: number): Promise<string[]> => {
  const { data } = await apiClient.get<{ competencies?: CompetencyTreeNode[] }>('/coaching/competency');
  const names: string[] = [];
  const walk = (nodes: CompetencyTreeNode[]) => {
    for (const node of nodes) {
      if (names.length >= limit) {
        return;
      }
      if (node.name) {
        names.push(node.name);
      }
      if (node.children?.length) {
        walk(node.children);
      }
    }
  };
  walk(data?.competencies ?? []);
  return names.slice(0, limit);
};

/**
 * Маппинг кода типа сущности онтологии на соответствующий раздел приложения.
 * Для типов вне маппинга примеры значений не показываются.
 */
export const ENTITY_TYPE_SECTIONS: Record<string, EntityTypeSection> = {
  attribute_def: {
    route: '/attributes',
    fetchSamples: (limit) => fetchFlatNames('/attribute-name', 'attributes', { limit }, limit),
  },
  competency: {
    route: '/competencies',
    fetchSamples: (limit) => fetchCompetencyNames(limit),
  },
  knowledge_node: {
    route: '/knowledge',
    fetchSamples: (limit) => fetchFlatNames('/knowledge', 'knowledgeNodes', { limit, isDeleted: false }, limit),
  },
  sample: {
    route: '/samples',
    fetchSamples: (limit) => fetchFlatNames('/sample', 'samples', { limit, isDeleted: false }, limit),
  },
  stop_word: {
    route: '/stop-words',
    fetchSamples: (limit) => fetchFlatNames('/stop-word', 'stopWords', { limit }, limit),
  },
  term: {
    route: '/terms',
    fetchSamples: (limit) => fetchFlatNames('/term', 'terms', { limit }, limit),
  },
  user_group: {
    route: '/user-groups',
    fetchSamples: (limit) => fetchFlatNames('/user-group', 'userGroups', { limit }, limit),
  },
  role: {
    route: '/roles',
    fetchSamples: (limit) => fetchFlatNames('/role', 'roles', {}, limit),
  },
};

/**
 * Возвращает описание раздела приложения для типа сущности либо `undefined`,
 * если для типа маппинг не задан.
 */
export const getEntityTypeSection = (entityTypeCode?: string): EntityTypeSection | undefined =>
  entityTypeCode ? ENTITY_TYPE_SECTIONS[entityTypeCode] : undefined;

/**
 * Хук примеров значений атрибута `name` для типа сущности: тянет первые
 * несколько названий из раздела приложения. Активен только для типов с маппингом.
 */
export const useEntityTypeSamples = (entityTypeCode?: string) => {
  const section = getEntityTypeSection(entityTypeCode);
  return useQuery({
    queryKey: ['ontology', 'entity-type-samples', entityTypeCode],
    queryFn: () => section!.fetchSamples(ENTITY_TYPE_SAMPLE_LIMIT),
    enabled: !!section,
    staleTime: 5 * 60 * 1000,
  });
};
